import React, { useState, useEffect } from 'react';

interface UserProfileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    name: string;
    position: string;
    department: string;
    avatar: string;
    coverImage: string;
    pronouns: string;
    location: string;
    experience: string;
    bio: string;
    workingSince: string;
  };
  recentActions: Array<{
    title: string;
    type: 'pdf' | 'design' | 'review';
  }>;
}

const UserProfileSidebar: React.FC<UserProfileSidebarProps> = ({
  isOpen,
  onClose,
  user,
  recentActions
}) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isContentChanging, setIsContentChanging] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);

  // Handle initial open animation and user changes
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle user change with full slide animation
  useEffect(() => {
    if (isOpen && user && currentUser && user.name !== currentUser.name) {
      // Trigger full slide animation for user change
      setIsAnimating(true);
      setIsContentChanging(true);
      
      // Slide out sidebar completely
      setTimeout(() => {
        setCurrentUser(user);
        setActiveTab('profile'); // Reset to profile tab when user changes
      }, 150);
      
      // Slide in sidebar with new content
      setTimeout(() => {
        setIsContentChanging(false);
        setIsAnimating(false);
      }, 300);
    } else if (user) {
      setCurrentUser(user);
    }
  }, [user, isOpen, currentUser]);

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return 'assets/pdf.svg';
      case 'design':
        return 'assets/review.svg';
      case 'review':
        return 'assets/pdf.svg';
      default:
        return 'assets/pdf.svg';
    }
  };

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      onClose();
      setIsAnimating(false);
    }, 200);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay with fade animation */}
      <div
        className={`fixed inset-0  transition-opacity duration-300 z-40 lg:hidden ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Sidebar with enhanced slide animation */}
      <div 
        className={`fixed right-0 top-[70px] sm:top-[80px] md:top-[100px] bottom-[17px] lg:bottom-[41px] w-full py-2 px-2 sm:w-96 lg:w-80 xl:w-96 bg-white shadow-xl z-50 overflow-y-auto
          transform transition-all duration-300 ease-in-out
          ${isOpen && !isAnimating && !isContentChanging ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        `}
        style={{
          transform: isOpen && !isAnimating && !isContentChanging ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out'
        }}
      >
        {/* Header with Cover Image - Enhanced with slide-in animation */}
        <div className={`relative h-28 sm:h-28 lg:h-24 xl:h-32 bg-gradient-to-br from-blue-400 to-blue-600 overflow-hidden rounded-xl
          transform transition-all duration-500 delay-100 ease-out
          ${isOpen && !isAnimating && !isContentChanging ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
        `}>
          <img
            src={currentUser.coverImage}
            alt="Cover"
            className="w-full h-full object-cover"
          />

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 w-8 h-8   rounded-full flex items-center justify-center transition-all duration-200 z-10 transform hover:scale-110"
          >
            <img src="assets/cross.svg" alt="Close" className="w-6 h-6" />
          </button>
        </div>

        {/* Profile Avatar - Enhanced with slide-in animation */}
        <div className={`relative -mt-8 lg:-mt-8 xl:-mt-10 px-6 z-20
          transform transition-all duration-500 delay-200 ease-out
          ${isOpen && !isAnimating && !isContentChanging ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
        `}>
          <div className="relative inline-block">
            <img
              src={currentUser.avatar}
              alt={currentUser.name}
              className="w-16 h-16 lg:w-16 lg:h-16 xl:w-[70px] xl:h-[70px] rounded-full border-2 border-white object-cover shadow-lg transition-transform duration-300 hover:scale-105"
            />
            <div className="absolute -bottom-1 -right-1 w-5 h-5 xl:w-6 xl:h-6 bg-white rounded-full border-2 border-white flex items-center justify-center transition-transform duration-300 hover:scale-110">
              <img src="assets/camera.svg" alt="Camera" className="w-2.5 h-2.5 lg:h-4 lg:w-4" />
            </div>
          </div>
        </div>

        {/* Profile Info Section - Enhanced with slide-in animation */}
        <div className={`px-6 pt-2 lg:pt-0 xl:pt-2 xl:pb-4 pb-3
          transform transition-all duration-500 delay-300 ease-out
          ${isOpen && !isAnimating && !isContentChanging ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
        `}>
          <div className="flex items-start justify-between mb-2 xl:mb-3">
            <div className="flex-1">
              <div className='flex gap-5 items-center'>
                <h2 className="text-lg xl:text-xl font-semibold text-black leading-tight">{currentUser.name}</h2>
                <button className="hover:bg-gray-100 rounded-full transition-all duration-200 p-1 hover:scale-110">
                  <img src="assets/edit.svg" alt="Edit" className="w-4 h-4 xl:w-5 xl:h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-600 mb-0.5 font-medium">{currentUser.pronouns}</p>
              <p className="text-sm font-medium text-black mb-0.5">{currentUser.position}</p>
              <p className="text-sm text-navgreen font-medium">{currentUser.department}</p>
            </div>
          </div>

          {/* Action Buttons - Enhanced with slide-in animation */}
          <div className="inline-flex gap-2 lg:mb-3 mb-4 w-full">
            <button className="flex-1 bg-navgreen hover:bg-navgreen hover:bg-opacity-90 text-white py-1.5 px-3 xl:py-2 xl:px-3 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:scale-105 hover:shadow-lg">
              <img src="assets/call-pro.svg" alt="Call" className="w-3 h-3 xl:w-4 xl:h-4" />
              <span className="text-xs xl:text-sm font-medium">Contact</span>
            </button>
            <button className="flex-1 bg-navgreen hover:bg-navgreen hover:bg-opacity-90 text-white py-1.5 px-2 xl:py-2 xl:px-2 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:scale-105 hover:shadow-lg">
              <img src="assets/mail.svg" alt="Email" className="w-3 h-3 xl:w-4 xl:h-4" />
              <span className="text-xs xl:text-sm font-medium">Email</span>
            </button>
          </div>

          {/* Details Section - Enhanced with slide-in animation */}
          <div className="space-y-2">
            {/* Location */}
            <div className="flex items-center gap-3 transition-all duration-200">
              <img src="assets/location-pro.svg" alt="Location" className="w-4 h-4 xl:w-5 xl:h-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs xl:text-sm font-medium text-deepTeal">Corporate - {currentUser.location}</p>
              </div>
            </div>

            {/* Experience */}
            <div className="flex items-center gap-3 transition-all duration-200">
              <img src="assets/clock-alt.svg" alt="Experience" className="w-4 h-4 xl:w-5 xl:h-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs xl:text-sm font-medium text-deepTeal">{currentUser.experience}</p>
              </div>
            </div>

            {/* Bio */}
            <div className="flex items-start gap-3 transition-all duration-200 ">
              <img src="assets/info.svg" alt="Info" className="w-4 h-4 xl:w-5 xl:h-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs xl:text-sm font-medium text-deepTeal leading-relaxed">{currentUser.bio}</p>
                <p className="text-xs xl:text-sm font-medium text-deepTeal mt-1">{currentUser.workingSince}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Actions Section - Enhanced with slide-in animation */}
        <div className={`px-6 lg:pb-3 pb-2
          transform transition-all duration-500 delay-400 ease-out
          ${isOpen && !isAnimating && !isContentChanging ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
        `}>
          <button
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-all duration-200 mb-2 xl:mb-3 w-full "
            onClick={() => setActiveTab(activeTab === 'actions' ? 'profile' : 'actions')}
          >
            <img
              src="assets/arrow.svg"
              alt="Toggle"
              className={`w-4 h-4 xl:w-5 xl:h-5 transform transition-all duration-300 ${activeTab === 'actions' ? 'rotate-90' : ''}`}
            />
            <span className="text-xs xl:text-sm font-medium text-deepTeal">Recent Actions</span>
          </button>

          {/* Actions List - Enhanced with staggered animations */}
          {activeTab === 'actions' && (
            <div className="space-y-2 py-1 px-2 max-h-36 max-w-96 lg:max-w-72 overflow-y-auto">
              {recentActions.map((action, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-all duration-300 group ml-4 shadow-[0px_0px_4px_0px_#00000040] hover:shadow-lg hover:scale-105
                    transform ${activeTab === 'actions' && !isAnimating && !isContentChanging ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}
                  `}
                  style={{
                    transitionDelay: `${index * 50}ms`
                  }}
                >
                  <span className="text-xs xl:text-sm text-navgreen font-medium flex-1 truncate group-hover:text-navgreen">
                    {action.title}
                  </span>
                  <img
                    src={getActionIcon(action.type)}
                    alt={action.type}
                    className="w-4 h-4 xl:w-5 xl:h-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserProfileSidebar;